import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  X,
  Save,
  Plus,
  Lock,
  Unlock,
  Calendar,
  AlertTriangle,
  Settings,
  ChevronDown,
  Trash2,
} from "lucide-react";
import { apiService } from "../services/api";
import { canViewSDLDetails, SDLPeriod, SDLPeriodCreate } from "../types";
import { useAuth } from "../hooks/useAuth";
import toast from "react-hot-toast";

interface SDLAssessmentEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  currentScore?: number | undefined;
  currentDate?: string | undefined;
  assessmentId?: string | undefined;
}

type TabId = "score" | "periods";

export default function SDLAssessmentEditModal({
  isOpen,
  onClose,
  productId,
  currentScore,
  currentDate,
  assessmentId,
}: SDLAssessmentEditModalProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>("score");

  const [formData, setFormData] = useState<{
    score: number | string;
    assessmentDate: string;
  }>({
    score: currentScore || 0,
    assessmentDate: currentDate || "",
  });

  const [validationErrors, setValidationErrors] = useState({
    score: "",
    assessmentDate: "",
  });

  const [newPeriod, setNewPeriod] = useState<SDLPeriodCreate>({
    year: new Date().getFullYear(),
    quarter: 1,
    isOpen: true,
  });

  const [deletingPeriodId, setDeletingPeriodId] = useState<string | null>(null);

  const {
    data: periodsData,
    isLoading: periodsLoading,
    error: periodsError,
  } = useQuery("sdlPeriods", apiService.getSDLPeriods, {
    enabled:
      isOpen &&
      (user?.roleId === "global_admin" ||
        user?.roleId === "ba_admin" ||
        user?.roleId === "division_admin"),
  });

  const periods: SDLPeriod[] = (periodsData?.data as any)?.data || [];

  const createPeriodMutation = useMutation(
    (data: SDLPeriodCreate) => apiService.createSDLPeriod(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sdlPeriods"]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods"]);
        toast.success("Period created successfully");
        setNewPeriod({
          year: new Date().getFullYear(),
          quarter: 1,
          isOpen: true,
        });
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to create period");
      },
    },
  );

  const togglePeriodMutation = useMutation(
    (periodId: string) => apiService.toggleSDLPeriod(periodId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sdlPeriods"]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods"]);
        toast.success("Period status updated");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to update period");
      },
    },
  );

  const setCurrentPeriodMutation = useMutation(
    (periodId: string) => apiService.setCurrentSDLPeriod(periodId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sdlPeriods"]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods"]);
        toast.success("Current period updated");
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to set current period",
        );
      },
    },
  );

  const deletePeriodMutation = useMutation(
    (periodId: string) => apiService.deleteSDLPeriod(periodId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sdlPeriods"]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods"]);
        toast.success("Period deleted successfully");
        setDeletingPeriodId(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to delete period");
        setDeletingPeriodId(null);
      },
    },
  );

  useEffect(() => {
    if (isOpen) {
      setFormData({
        score: currentScore || 0,
        assessmentDate: currentDate || "",
      });
      setValidationErrors({ score: "", assessmentDate: "" });
    }
  }, [isOpen, currentScore, currentDate]);

  const updateSDLAssessmentMutation = useMutation(
    (data: any) => apiService.createSDLAssessmentAdminOverride(productId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assessmentStatus", productId]);
        queryClient.invalidateQueries(["product", productId]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods", productId]);
        queryClient.invalidateQueries(["sdlAssessment", assessmentId]);
        toast.success("SDL Assessment updated successfully");
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to update SDL Assessment",
        );
      },
    },
  );

  const handleScoreSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors({ score: "", assessmentDate: "" });

    let hasErrors = false;
    const errors = { score: "", assessmentDate: "" };

    const scoreValue =
      typeof formData.score === "string"
        ? parseFloat(formData.score)
        : formData.score;
    if (
      formData.score === "" ||
      isNaN(scoreValue) ||
      scoreValue < 0 ||
      scoreValue > 4
    ) {
      errors.score = "SDL Maturity Score must be between 0.0 and 4.0";
      hasErrors = true;
    }

    if (!formData.assessmentDate) {
      errors.assessmentDate = "Assessment date is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setValidationErrors(errors);
      toast.error("Please fix the validation errors");
      return;
    }

    const finalScore =
      typeof formData.score === "string"
        ? parseFloat(formData.score)
        : formData.score;
    const assessmentData = {
      period: "Admin-" + formData.assessmentDate,
      version: "1.0",
      answers: [],
      status: "submitted",
      adminScore: finalScore,
      completedAt: formData.assessmentDate,
    };

    updateSDLAssessmentMutation.mutate(assessmentData);
  };

  const handleCreatePeriod = () => {
    if (!newPeriod.year || !newPeriod.quarter) {
      toast.error("Please select both year and quarter");
      return;
    }
    createPeriodMutation.mutate(newPeriod);
  };

  if (!canViewSDLDetails(user)) {
    return null;
  }

  if (!isOpen) return null;

  const isPeriodAdmin =
    user?.roleId === "global_admin" ||
    user?.roleId === "ba_admin" ||
    user?.roleId === "division_admin";
  const tabs: { id: TabId; label: string; icon: JSX.Element }[] = [
    {
      id: "score",
      label: "Score Override",
      icon: <Save className="h-3.5 w-3.5" />,
    },
    ...(isPeriodAdmin
      ? [
          {
            id: "periods" as TabId,
            label: "Manage Periods",
            icon: <Settings className="h-3.5 w-3.5" />,
          },
        ]
      : []),
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            SDL Admin
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {tabs.length > 1 && (
          <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "score" && (
            <form onSubmit={handleScoreSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="score"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  SDL Maturity Score
                </label>
                <input
                  type="number"
                  id="score"
                  name="score"
                  value={formData.score}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue === "") {
                      setFormData((prev) => ({ ...prev, score: "" as any }));
                      setValidationErrors((prev) => ({ ...prev, score: "" }));
                      return;
                    }
                    const value = parseFloat(inputValue);
                    if (!isNaN(value) && value >= 0 && value <= 4) {
                      setFormData((prev) => ({ ...prev, score: value }));
                      setValidationErrors((prev) => ({ ...prev, score: "" }));
                    } else if (!isNaN(value)) {
                      const clampedValue = Math.max(0, Math.min(4, value));
                      setFormData((prev) => ({ ...prev, score: clampedValue }));
                      setValidationErrors((prev) => ({ ...prev, score: "" }));
                    }
                  }}
                  className={`input ${validationErrors.score ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""} [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                  placeholder="0.0 - 4.0"
                  required
                />
                {validationErrors.score && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.score}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Score must be between 0.0 and 4.0
                </p>
              </div>

              <div>
                <label
                  htmlFor="assessmentDate"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Assessment Date
                </label>
                <input
                  type="date"
                  id="assessmentDate"
                  name="assessmentDate"
                  value={formData.assessmentDate}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      assessmentDate: e.target.value,
                    }));
                    setValidationErrors((prev) => ({
                      ...prev,
                      assessmentDate: "",
                    }));
                  }}
                  className={`input ${validationErrors.assessmentDate ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                  required
                />
                {validationErrors.assessmentDate && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.assessmentDate}
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateSDLAssessmentMutation.isLoading}
                  className="flex-1 btn btn-primary"
                >
                  {updateSDLAssessmentMutation.isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {activeTab === "periods" && isPeriodAdmin && (
            <div className="space-y-5">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Add New Period
                </h3>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Year
                    </label>
                    <select
                      value={newPeriod.year}
                      onChange={(e) =>
                        setNewPeriod((prev) => ({
                          ...prev,
                          year: parseInt(e.target.value),
                        }))
                      }
                      className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - 2 + i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quarter
                    </label>
                    <select
                      value={newPeriod.quarter}
                      onChange={(e) =>
                        setNewPeriod((prev) => ({
                          ...prev,
                          quarter: parseInt(e.target.value),
                        }))
                      }
                      className="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value={1}>Q1</option>
                      <option value={2}>Q2</option>
                      <option value={3}>Q3</option>
                      <option value={4}>Q4</option>
                    </select>
                  </div>
                  <button
                    onClick={handleCreatePeriod}
                    disabled={createPeriodMutation.isLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {createPeriodMutation.isLoading ? "Adding..." : "Add"}
                  </button>
                </div>
                <label className="mt-2.5 inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!newPeriod.isOpen}
                    onChange={(e) =>
                      setNewPeriod((prev) => ({
                        ...prev,
                        isOpen: e.target.checked,
                      }))
                    }
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Open this period immediately
                </label>
              </div>

              {periodsLoading ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Loading periods...
                  </p>
                </div>
              ) : periodsError ? (
                <div className="text-center py-6">
                  <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Failed to load periods
                  </p>
                </div>
              ) : periods.length === 0 ? (
                <div className="text-center py-6">
                  <Calendar className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No periods yet. Create the first one above.
                  </p>
                </div>
              ) : (
                <>
                  {/* Active Period Dropdown */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                    <label className="block text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1.5 uppercase tracking-wider">
                      Active Assessment Period
                    </label>
                    <div className="relative">
                      <select
                        value={
                          periods.find((p: SDLPeriod) => p.isCurrent)?.id || ""
                        }
                        onChange={(e) => {
                          if (e.target.value) {
                            setCurrentPeriodMutation.mutate(e.target.value);
                          }
                        }}
                        disabled={setCurrentPeriodMutation.isLoading}
                        className="block w-full pl-3 pr-8 py-2 text-sm font-medium border border-blue-300 dark:border-blue-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none disabled:opacity-50 cursor-pointer"
                      >
                        <option value="" disabled>
                          Select active period…
                        </option>
                        {periods.map((p: SDLPeriod) => (
                          <option key={p.id} value={p.id}>
                            {p.year} Q{p.quarter}{" "}
                            {p.isOpen ? "(Open)" : "(Closed)"}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 dark:text-blue-400 pointer-events-none" />
                    </div>
                    <p className="text-[11px] text-blue-600/70 dark:text-blue-400/70 mt-1.5">
                      This period is shown as "NOW" on product SDL dashboards.
                    </p>
                  </div>

                  {/* Periods Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Period
                          </th>
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="text-right py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {periods.map((period: SDLPeriod) => (
                          <tr
                            key={period.id}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${period.isCurrent ? "bg-blue-50/50 dark:bg-blue-900/10" : ""}`}
                          >
                            <td className="py-2.5 pr-4">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {period.year} Q{period.quarter}
                                </span>
                                {period.isCurrent && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                    NOW
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 pr-4">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${period.isOpen ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"}`}
                              >
                                {period.isOpen ? "Open" : "Closed"}
                              </span>
                            </td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-1.5 relative">
                                <button
                                  onClick={() =>
                                    togglePeriodMutation.mutate(period.id)
                                  }
                                  disabled={togglePeriodMutation.isLoading}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-white transition-colors disabled:opacity-50 ${period.isOpen ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}
                                >
                                  {period.isOpen ? (
                                    <>
                                      <Lock className="h-3 w-3" />
                                      Close
                                    </>
                                  ) : (
                                    <>
                                      <Unlock className="h-3 w-3" />
                                      Open
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    !period.isOpen &&
                                    setDeletingPeriodId(
                                      deletingPeriodId === period.id
                                        ? null
                                        : period.id,
                                    )
                                  }
                                  disabled={period.isOpen}
                                  className={`inline-flex items-center p-1 rounded-md transition-colors ${
                                    period.isOpen
                                      ? "text-gray-200 dark:text-gray-700 cursor-not-allowed"
                                      : "text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 cursor-pointer"
                                  }`}
                                  title={
                                    period.isOpen
                                      ? "Close the period before deleting"
                                      : "Delete period"
                                  }
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                                {/* Delete Confirmation Popover */}
                                {deletingPeriodId === period.id && (
                                  <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg shadow-xl p-3">
                                    <div className="flex items-start gap-2 mb-2">
                                      <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                                          Delete {period.year} Q{period.quarter}
                                          ?
                                        </p>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                          This will also remove all draft
                                          assessments for this period.
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                      <button
                                        onClick={() =>
                                          setDeletingPeriodId(null)
                                        }
                                        className="flex-1 px-2 py-1 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() =>
                                          deletePeriodMutation.mutate(period.id)
                                        }
                                        disabled={
                                          deletePeriodMutation.isLoading
                                        }
                                        className="flex-1 px-2 py-1 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                                      >
                                        {deletePeriodMutation.isLoading
                                          ? "Deleting…"
                                          : "Delete"}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
